import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { parseFloatData } from "utils/UtilityFuncs";
import NunitoBold from "assets/fonts/Nunito-Bold.ttf";
import NunitoMedium from "assets/fonts/Nunito-Medium.ttf";
import NunitoRegular from "assets/fonts/Nunito-Regular.ttf";
import NunitoSemiBold from "assets/fonts/Nunito-SemiBold.ttf";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: "Nunito",
    fontSize: 10,
  },
  title: {
    fontSize: 22,
    marginBottom: 0,
    paddingBottom: 0,
    lineHeight: 1,
    textAlign: "center",
    fontWeight: "bold",
  },
  titleMd: {
    fontSize: 18,
    textAlign: "center",
    fontWeight: "bold",
  },

  headerLine: {
    fontSize: 14,
    marginBottom: 2,
    textAlign: "start",
    fontWeight: "bold",
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  headerCell: {
    fontWeight: "bold",
    backgroundColor: "#eeeeee",
  },
  cell: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
    flexGrow: 1,
  },
  cellSmall: {
    width: 30,
    flexGrow: 0,
  },
  cellMedium: {
    width: 80,
    flexGrow: 0,
  },
  cellLarge: {
    width: 120,
    flexGrow: 0,
  },
});

export default function MShipmentReportPdf({ shipments, licenseInfo }) {
  Font.register({
    family: "Nunito",
    fonts: [
      { src: NunitoRegular },
      { src: NunitoSemiBold, fontWeight: "600" },
      { src: NunitoBold, fontWeight: "bold" },
      { src: NunitoMedium, fontWeight: "medium" },
    ],
  });
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Text style={styles.title}>Detail of Import Shipments</Text>
        <Text style={[styles.title, { fontSize: 19, marginBottom: 4 }]}>
          Super Nova Sports, Sialkot
        </Text>

        {licenseInfo && (
          <>
            <Text style={styles.titleMd}>
              E.F.S Licence # {licenseInfo.licenseId}
            </Text>
            <Text style={styles.headerLine}>({licenseInfo.sro})</Text>
            <Text
              style={{
                ...styles.headerLine,
                marginBottom: 12,
              }}
            >
              {licenseInfo.limit}
            </Text>
          </>
        )}

        <View style={styles.table}>
          {/* Header */}
          <View style={styles.tableRow}>
            <View style={[styles.cell, styles.cellSmall, styles.headerCell]}>
              <Text>S. No</Text>
            </View>
            <View style={[styles.cell, styles.cellLarge, styles.headerCell]}>
              <Text>B/E No</Text>
            </View>
            <View style={[styles.cell, styles.cellLarge, styles.headerCell]}>
              <Text>Custom Port</Text>
            </View>
            <View style={[styles.cell, styles.cellMedium, styles.headerCell]}>
              <Text>B/E DT</Text>
            </View>
            <View style={[styles.cell, styles.cellLarge, styles.headerCell]}>
              <Text>Item</Text>
            </View>
            <View style={[styles.cell, styles.cellMedium, styles.headerCell]}>
              <Text>Total Qty</Text>
            </View>
            <View style={[styles.cell, styles.cellLarge, styles.headerCell]}>
              <Text>Accessed Value</Text>
            </View>
            <View style={[styles.cell, styles.cellLarge, styles.headerCell]}>
              <Text>Accessed Value PKR</Text>
            </View>
            <View style={[styles.cell, styles.cellMedium, styles.headerCell]}>
              <Text>Expiry Date</Text>
            </View>
          </View>

          {/* Body */}
          {shipments && shipments.length > 0 ? (
            shipments.map((ship, sIndex) => {
              const items =
                ship.items && ship.items.length ? ship.items : [null];
              const shipmentSNo = sIndex + 1;
              const itemCount = items.length;

              return items.map((item, itemIndex) => {
                const isFirst = itemIndex === 0;
                const isLast = itemIndex === itemCount - 1;
                const spanBorders =
                  itemCount > 1
                    ? {
                        // single line at the top of the group and
                        // single line at the bottom of the group
                        borderTopWidth: isFirst ? 0 : 0,
                        borderBottomWidth: isLast ? 1 : 0,
                      }
                    : {};

                return (
                  <View
                    key={`${ship.id || ship.shipmentId}-${itemIndex}`}
                    style={styles.tableRow}
                  >
                    {/* Shipment-level cells only on first row for that shipment, with merged-style borders */}
                    <View style={[styles.cell, styles.cellSmall, spanBorders]}>
                      {isFirst && <Text>{shipmentSNo}</Text>}
                    </View>
                    <View
                      style={[
                        styles.cell,
                        styles.cellLarge,
                        spanBorders,
                        // { backgroundColor: "#F0F0F0" },
                      ]}
                    >
                      {isFirst && <Text>{ship.b_e_no || "-"}</Text>}
                    </View>
                    <View style={[styles.cell, styles.cellLarge, spanBorders]}>
                      {isFirst && <Text>{ship.port || "-"}</Text>}
                    </View>
                    <View style={[styles.cell, styles.cellMedium, spanBorders]}>
                      {isFirst && (
                        <Text>
                          {ship.b_e_date
                            ? new Date(ship.b_e_date).toLocaleDateString()
                            : "-"}
                        </Text>
                      )}
                    </View>

                    {/* Item-level cells every row */}
                    <View style={[styles.cell, styles.cellLarge]}>
                      <Text>{item && item.name ? item.name : "-"}</Text>
                    </View>
                    <View style={[styles.cell, styles.cellMedium]}>
                      <Text>
                        {item && item.totalQty != null
                          ? parseFloatData(item.totalQty, 4)
                          : "-"}
                      </Text>
                    </View>
                    <View style={[styles.cell, styles.cellLarge]}>
                      <Text>
                        {item && item.currencyType
                          ? item.currencyType + " "
                          : ""}
                        {item && item.assessedValue != null
                          ? parseFloatData(item.assessedValue, 4)
                          : "-"}
                      </Text>
                    </View>
                    <View style={[styles.cell, styles.cellLarge]}>
                      <Text>
                        {item && item.assessedValuePkr != null
                          ? parseFloatData(item.assessedValuePkr, 4)
                          : "-"}
                      </Text>
                    </View>
                    <View style={[styles.cell, styles.cellMedium, spanBorders]}>
                      {isFirst && (
                        <Text>
                          {ship.expiryDate
                            ? new Date(ship.expiryDate).toLocaleDateString()
                            : "-"}
                        </Text>
                      )}
                    </View>
                  </View>
                );
              });
            })
          ) : (
            <View style={styles.tableRow}>
              <View style={[styles.cell]}>
                <Text>No data</Text>
              </View>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
}
